import Injector from '../core/injector';
import Logger from '../utilities/logger';
/** Asserts if we've got an injectable param set. */
const isInjectableParams = (value) => typeof value !== 'string' && value.scope !== undefined;
/** Asserts if we have a module key and not a param set. */
const isModuleKey = (value) => typeof value === 'string';
/** Asserts if we have a dependency set. That set takes the form of a object literal with any members. */
const isDependencies = (value) => typeof value === 'object';
/**
 * This is the implementation of the overload.
 *
 * @param first The first argument
 * @param second The second argument
 */
export function injectable(first, second) {
    if (isInjectableParams(first)) {
        return (target) => {
            const { key, scope, dependencies } = first;
            Injector.register({
                key,
                dependencies,
                scope,
                constructor: target,
            });
        };
    }
    if (isModuleKey(first)) {
        return (target) => {
            Injector.register({
                key: first,
                dependencies: isDependencies(second) ? second : {},
                constructor: target,
            });
        };
    }
    Logger.error(`Invalid signatures provided for the register call first arg: ${first} second arg: ${second}`);
    throw new Error('invalid signatures provided for the register call');
}
// eslint-disable-next-line @typescript-eslint/ban-types
export const buildInjectFunctionCall = (originalMethod, dependencyInstances) => function value(...params) {
    let spreadableArguments = [...params];
    if (params.length === originalMethod.length) {
        spreadableArguments = params.slice(0, params.length - dependencyInstances.length);
    }
    return originalMethod.call(this, ...spreadableArguments, ...dependencyInstances);
};
/**
 * Inject provided dependencies into a method as the last parameters in the function signature
 *
 * @param dependencies Dependencies required to inject into the method
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const inject = (dependencies) => (target, propertyKey, descriptor) => {
    // Create a backup reference to the original function for calling it manually later
    const originalMethod = descriptor.value;
    // Get the dependencies to be injected
    const dependencyInstances = Object.keys(dependencies).map((key) => Injector.get(key, dependencies[key]));
    // Replace the actual function expression with a custom one that injects the dependencies into
    // the function reference above
    // eslint-disable-next-line no-param-reassign
    descriptor.value = buildInjectFunctionCall(originalMethod, dependencyInstances);
    return descriptor;
};
